
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { countryList, s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { RequestProvider } from '../../../context/RequestContext'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { Services } from '../../../components/index/service/Services'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'
import { AirconProblems } from '@/index/service-category/aircon-service/AirconProblems'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

    const getLocalityList = () => {
        for(let x in countryList){
            if(countryList[x].country_name === countryName){
                return countryList[x].localityList
            }
        }
    } 

    const localityList = getLocalityList()

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        countryName,
        currencyCd,
        wpNewPosts,
        wpPosts,
        localityList
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestType = 'Aircon Services'
    const requestCategorySettings = props.requestCategorySettings
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const localityList = props.localityList
    const countryName = props.countryName

    const maxRating = [1, 2, 3, 4, 5]
    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Affordable Aircon Repair In Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Need aircon repair in Singapore? StringsSG provides air conditioner repair and troubleshooting services for homes and businesses. We have a team of experienced professionals who provide our customers with the best possible experience."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-repair" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Affordable Aircon Repair In Singapore`}/>
        <meta property="og:description" content="Is your air conditioner not working properly? Get a professional technician in Singapore for affordable Aircon repair and troubleshoot." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-02T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon-installation.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon-installation.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-repair"/>
        <meta name="twitter:title" content={`Affordable Aircon Repair In Singapore`}/> 
        <meta name="twitter:description" content="Is your air conditioner not working properly? Get a professional technician in Singapore for affordable Aircon repair and troubleshoot."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-repair"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
            path: '/sg/aircon-servicing-singapore/aircon-repair',
            pageName: 'Affordable Aircon Repair In Singapore',
            description: 'Need aircon repair in Singapore? StringsSG provides air conditioner repair and troubleshooting services for homes and businesses. We have a team of experienced professionals who provide our customers with the best possible experience.',
            serviceName: 'Aircon Repair Singapore',
            serviceType: 'Aircon Repair',
            image: 'https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon-installation.jpg',
            datePublished: '2025-01-02T07:30:46+00:00',
            modifiedDate,
            breadcrumbName: 'Aircon Repair',
          })) }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-repair" className='relative'>    
                    <AirconRepair 
                        requestCategorySettings={requestCategorySettings} 
                        setRequestForm={setRequestForm} 
                        standardServicing={standardServicing} 
                        setIsQuickBook={setIsQuickBook} 
                        currencyCd={currencyCd} 
                        title={"Affordable Aircon Repair In Singapore"} problem={""}
                        subtitle="Our vetted technicians diagnose the fault before any repair begins. Same-day slots available."
                        heroImage="blog/living/aircon-installation.jpg"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='leading-relaxed'>                
                <div className='py-5 px-2 space-y-2 max-w-5xl mx-auto'>
                    <section className="py-6 md:py-10 space-y-6 text-gray-800">
                        {/* Intro: Why Aircon Repairs Are Common in Singapore */}
                        <header className=" space-y-4 text-lg leading-relaxed">
                            <p>
                            Singapore&apos;s hot and humid tropical climate—with temperatures averaging 26°C to 34°C and humidity levels reaching 100%—makes air conditioning a daily necessity for most residents. 
                            </p>
                            <p>
                            Due to this constant use, it&apos;s common for air conditioning systems to develop faults over time. Below, we explore the common reasons why your aircon may need repair in Singapore.
                            </p>
                        </header>

                        {/* Common Reasons for Aircon Faults */}
                        <div className="space-y-6 text-base px-2">
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 border-b pb-3">
                            Common Reasons Your Aircon Needs Repair
                            </h2>
                            <ul className="space-y-4 list-decimal list-inside">
                            <li>
                                <strong>Lack of Maintenance:</strong> Without regular cleaning and servicing, dirt and dust build up inside the unit, affecting performance and causing system failures.
                            </li>
                            <li>
                                <strong>Wear and Tear:</strong> Components like compressors, motors, and fans naturally degrade over time due to continuous operation.
                            </li>
                            <li>
                                <strong>Electrical Issues:</strong> Problems like blown fuses, faulty wiring, or tripped breakers can prevent the system from operating properly.
                            </li>
                            <li>
                                <strong>Refrigerant Leaks:</strong> Low refrigerant levels reduce cooling performance. Leaks often occur in the piping, evaporator coil, or condenser unit.
                            </li>
                            <li>
                                <strong>Old Age:</strong> As systems age, they become less efficient and more prone to breakdowns—especially if they&apos;re not maintained regularly.
                            </li>
                            </ul>
                        </div>
                    </section>

                    <section className="bg-gray-50 py-12">
                        <div className="max-w-5xl mx-auto space-y-10 px-4">
                            <h2 className="text-center text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-wide">
                            Signs Your Aircon Needs Repair
                            </h2>
                            <p className="text-center max-w-2xl mx-auto text-lg text-gray-700">
                            Watch out for these common signs that indicate it&apos;s time to book an aircon repair service:
                            </p>

                            {/* Grid of Symptoms */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                                {
                                title: 'Poor Airflow',
                                text: 'Weak airflow may be due to clogged filters or a failing motor.'
                                },
                                {
                                title: 'Noisy Operation',
                                text: 'Rattling, squeaking, or grinding sounds often signal loose parts or motor issues.'
                                },
                                {
                                title: 'Foul Smells',
                                text: 'A musty or moldy odor may be caused by a dirty filter or mold buildup inside the unit.'
                                },
                                {
                                title: 'Water Leakage',
                                text: 'Leaks can occur from clogged drainpipes, a cracked condenser, or refrigerant issues.'
                                },
                                {
                                title: 'Not Cooling Properly',
                                text: 'If warm air is blowing, it could be a refrigerant leak or a broken compressor.'
                                },
                                {
                                title: 'High Electricity Bills',
                                text: 'Unusually high bills may indicate your unit is overworking due to inefficiency or faults.'
                                },
                                {
                                title: 'Frequent Shut-Offs or Won’t Turn On',
                                text: 'Could be caused by overheating, dirty coils, or electrical problems.'
                                },
                                {
                                title: 'Blinking Indicator Lights',
                                text: 'Often signals internal faults like motor issues or sensor malfunctions—refer to your unit’s manual.'
                                }
                            ].map(({ title, text }, i) => (
                                <div
                                key={i}
                                className="p-3.5 rounded-xl bg-white space-y-1.5 border border-gray-200"
                                >
                                <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">
                                    {title}
                                </h3>
                                <p className="text-sm text-gray-600">{text}</p>
                                </div>
                            ))}
                            </div>
                        </div>
                    </section>
                    <AirconProblems/> 
                    <section className='p-2 space-y-2'>
                        <h2 className='text-2xl md:text-3xl font-bold'>Aircon Repair Costs in Singapore</h2>
                        <div className='space-y-2'>
                            <p>
                            Before starting any repairs, a technician will conduct a thorough assessment to identify the root cause of the issue. Once diagnosed, you will receive a quotation for the repair. In some cases, replacing your aircon may be more cost-effective than repairing it.
                            </p>
                            <p>
                            Often, common issues can be resolved immediately with deep cleaning and minor parts replacement.
                            </p>
                            <p>
                            Once the unit is repaired, be sure to test its cooling, oscillation, and remote control functions before the technicians leave.
                            </p>
                        </div>
                    </section> 
                    <section className='space-y-3 pt-6'>
                        <h2 className='text-2xl md:text-3xl font-bold uppercase text-center'>DIY Aircon Repair: Common Error Codes</h2>
                        <p className='text-sm text-gray-600'>Learn how to identify and fix common aircon problems using error codes displayed on your unit.</p>
                        <div className='border border-slate-200 rounded-lg divide-y divide-slate-200 overflow-hidden'>
                        {[
                            {
                            code: 'E1',
                            issue: 'Problem with indoor or outdoor temperature sensor.',
                            fix: 'Turn off the aircon, remove the indoor unit cover, clean the temperature sensor with a soft cloth, then turn it back on.'
                            },
                            {
                            code: 'E2',
                            issue: 'Issue with the pipe temperature sensor.',
                            fix: 'Check and refill refrigerant gas if levels are low.'
                            },
                            {
                            code: 'E3',
                            issue: 'Discharge temperature sensor issue.',
                            fix: 'Clean air filters and remove any duct blockages.'
                            },
                            {
                            code: 'E4',
                            issue: 'Condenser unit problem.',
                            fix: 'Inspect outdoor unit for debris or damage; check if fan is spinning freely.'
                            },
                            {
                            code: 'E5',
                            issue: 'Temperature sensor or thermistor fault.',
                            fix: 'Contact a technician to diagnose and replace the faulty part.'
                            },
                            {
                            code: 'E6',
                            issue: 'Fan motor issue.',
                            fix: 'Check motor wiring, clean fan blades, or replace the control board if necessary.'
                            },
                            {
                            code: 'E7',
                            issue: 'Compressor mode mismatch or fault.',
                            fix: 'Try resetting the unit. If issue persists, the compressor may need replacement.'
                            },
                            {
                            code: 'E8',
                            issue: 'Usually related to pipe temperature sensor, varies by model.'
                            },
                            {
                            code: 'E9',
                            issue: 'Communication circuit issue.',
                            fix: 'Check wiring and power, then reset the unit.'
                            },
                            {
                            code: 'F1',
                            issue: 'Indoor ambient temperature sensor fault.'
                            },
                            {
                            code: 'F2',
                            issue: 'Room temperature sensor issue.'
                            },
                            {
                            code: 'F3',
                            issue: 'Heat exchanger temperature sensor problem.'
                            },
                            {
                            code: 'F4',
                            issue: 'Suction temperature sensor issue.'
                            },
                            {
                            code: 'F5',
                            issue: 'Discharge temperature sensor issue.'
                            },
                            {
                            code: 'F6',
                            issue: 'Water temperature sensor fault.'
                            },
                            {
                            code: 'F7',
                            issue: 'Outdoor ambient temperature sensor fault.'
                            }
                        ].map(({ code, issue, fix }, i) => (
                            <div key={code}
                                className={`flex items-start gap-3 p-3 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                            >
                                <span className='shrink-0 w-11 text-center font-bold text-orange-600'>{code}</span>
                                <div className='space-y-1 text-sm'>
                                    <p className='text-gray-800'>{issue}</p>
                                    <p className='text-gray-600'>
                                        <span className='font-semibold text-gray-800'>Fix - </span>
                                        {fix || 'Consult a technician to diagnose the exact fault.'}
                                    </p>
                                </div>
                            </div>
                        ))}
                        </div>

                        <p className='text-sm text-gray-600 pt-2'>
                            If your issue persists or you&apos;re unsure about the repairs, it&apos;s best to consult a professional. Technicians have the tools and training to ensure accurate diagnostics and safe repairs.
                        </p>
                    </section>
                </div> 
                <div className='max-w-5xl mx-auto py-6 md:py-8 space-y-4'>
                    <div className='relative max-w-3xl mx-auto h-52 md:h-80'>
                        <Image
                        className='rounded-2xl'
                        src={s3Static + "blog/living/aircon/ac-not-cooling.jpg"}
                        fill
                        style={{ objectFit: 'cover', objectPosition: 'top' }}
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='young couple thinking of aircon repair'
                        sizes="(max-width: 768px) 100vw, 768px"
                        />
                    </div>
                    <div className='p-2 space-y-5'>
                        <h3 className='text-xl md:text-2xl uppercase text-center font-bold'>DIY Troubleshooting: Aircon Won’t Turn On</h3>
                        <p>Follow these steps to identify why your air conditioner isn’t powering up:</p>
                        <ul className='space-y-2 list-decimal list-inside'>
                        <li><strong>Power Supply:</strong> Ensure the unit is plugged in and the outlet is functional. Try another outlet if unsure.</li>
                        <li><strong>Remote or Thermostat:</strong> Check if the remote has power. Replace batteries if the display is blank.</li>
                        <li><strong>Circuit Breaker:</strong> Look for tripped breakers in your electrical panel and reset them if needed.</li>
                        <li><strong>Internal Components:</strong> If basic checks don’t resolve the issue, there may be a problem with the compressor, capacitor, or motor. Contact a professional for repairs.</li>
                        </ul>
                    </div>
                </div>
            </div>
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
